import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import useGameCompetitor from '@op/pages/competitor/hooks/useGameCompetitor';
import useGameCountryPublisher from '@op/pages/competitor/hooks/useGameCountryPublisher';
import {
  getCompareCompetitorsGrouping,
  getCompareCompetitorsKeyMetrics,
  getCompareCompetitorsTimeline,
} from '@op/services/compare';
import { gameInfoType } from '@op/types/common';
import { cloneDeep } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';

export function useCompareGameInfo({
  gameInfo,
  defaultValue,
}: {
  gameInfo: gameInfoType;
  defaultValue: Array<any>;
}) {
  const [visible, setVisible] = useState(false);

  const {
    mainGame,
    competitorGame,
    originalGameList,
    changeCompetitorGame,
    deleteCompetitorGame,
    loading,
  } = useGameCompetitor({
    filter: gameInfo,
    gameInfo,
    requestGame: false,
    defaultSelectGame: defaultValue,
  });

  const { gameCountryMap } = useGameCountryPublisher({
    selected: originalGameList,
    mainGame,
  });

  return {
    mainGame,
    competitorGame,
    changeCompetitorGame,
    deleteCompetitorGame,
    loading,
    gameCountryMap,
    visible,
    setVisible,
    originalGameList,
  };
} 
export function useCompareCompetitorsKeyMetrics({ filter, formatData }: any) {
  const [data, setData] = useState<any>([]);
  // const [error, setError] = useState(false);
  const { loading, wrapperRequest, error } = useCancelRequestHook();
  function requestKeyMetrics() {
    if (!filter?.compare_games) {
      return;
    }
    wrapperRequest((cancelFunc) =>
      getCompareCompetitorsKeyMetrics(filter, cancelFunc).then((data: any) => {
        const formattedData = formatData?.(data, filter) || data;
        setData(formattedData);
      }),
    );
  }

  useEffect(() => {
    requestKeyMetrics();
  }, [filter]);

  return { data, loading, error, refetch: requestKeyMetrics };
}

type ApiState = {
  data: any | null;
  loading: boolean;
  error: any | null;
};

export function useCompareCompetitorsKeyMetricsGrouping({
  filter,
  formatData,
}: any) {
  const [state, setState] = useState<Record<string, ApiState>>({});
  const { loading, wrapperRequest } = useCancelRequestHook();
  const requestList = useRef([]);

  function requestKeyMetricsGrouping() {
    const item = requestList.current.shift();
    if (!item) {
      return;
    }
    const { edition_unified_id, start_time, end_time, index, ...res } = item;
    const uniqueKey = `${edition_unified_id}_${start_time}_${end_time}_${index}`;
    const filterParams = {
      ...filter,
      compare_games: [{ edition_unified_id, start_time, end_time, ...res }],
    };

    wrapperRequest((cancelFunc) =>
      getCompareCompetitorsGrouping(filterParams, cancelFunc).then(
        (data: any) => {
          const formattedData = formatData?.(data, filterParams) || data;
          setState((prevState) => ({
            ...prevState,
            [uniqueKey]: {
              ...prevState[uniqueKey],
              data: formattedData,
              loading: false,
            },
          }));
          requestKeyMetricsGrouping();
        },
      ),
    );
  }

  useEffect(() => {
    if (!filter?.group_by) {
      return;
    }

    requestList.current = cloneDeep(
      filter.compare_games.map((val, index) => ({
        ...val,
        index,
      })),
    );
    requestList.current.forEach((key, index) => {
      const { edition_unified_id, start_time, end_time } = key || {};
      setState((prevState) => ({
        ...prevState,
        [`${edition_unified_id}_${start_time}_${end_time}_${index}`]: {
          data: null,
          loading: true,
          error: false,
        },
      }));
    });

    requestKeyMetricsGrouping();
  }, [filter]);

  return {
    loading,
    refetch: requestKeyMetricsGrouping,
    state,
  };
}

export function useCompareCompetitorsTimeline({
  filter,
  formatData,
  extraData,
}: any) {
  const [timeline, setTimeline] = useState<any>({});
  const { loading, wrapperRequest, error } = useCancelRequestHook();
  function requestTimeline() {
    if (!filter?.compare_games) {
      return;
    }
    wrapperRequest((cancelFunc) =>
      getCompareCompetitorsTimeline(filter, cancelFunc).then((data: any) => {
        const formattedData = formatData?.(data, filter, extraData) || data;
        setTimeline(formattedData);
      }),
    );
  }

  useEffect(() => {
    requestTimeline();
  }, [filter]);

  return { data: timeline, loading, error, refetch: requestTimeline };
}
